import React, { useMemo, useState } from "react";
import { ArrowLeft, RotateCcw, FileDown, Trophy, Target, NotebookPen, Loader2 } from "lucide-react";
import QuestionCard from "./QuestionCard";

export function examScore(exam, ans = {}) {
  let correct = 0, wrong = 0;
  exam.questions.forEach((q, i) => {
    if (ans[i] === undefined) return;
    if (ans[i] === q.a) correct++;
    else wrong++;
  });
  return { correct, wrong, answered: correct + wrong, total: exam.questions.length };
}

export default function ExamView({ course, answers, setAnswers, mistakes, setMistakes, examId, setExamId, onPdf, pdfBusy, goMistakes }) {
  const exam = course.exams.find((e) => e.id === examId);
  const [filter, setFilter] = useState("all");

  const ans = useMemo(() => (exam ? answers[exam.id] || {} : {}), [answers, exam]);
  const sc = exam ? examScore(exam, ans) : null;

  if (!exam) {
    return (
      <div>
        <div className="section-head">
          <span className="eyebrow">{course.code} · Deneme Sınavları</span>
          <h1 className="page-title">Vize Deneme Sınavları</h1>
          <p className="page-sub">Her sınav 25 çoktan seçmeli sorudan oluşur. Şıkkı işaretlediğinde anında kontrol edilir; yanlışlar otomatik olarak Hata Defterine eklenir.</p>
        </div>
        <div className="exam-pick">
          {course.exams.map((ex, idx) => {
            const s = examScore(ex, answers[ex.id]);
            const calc = ex.questions.filter((q) => q.s).length;
            return (
              <button key={ex.id} className="exam-tile" onClick={() => setExamId(ex.id)} data-testid={`exam-open-${idx + 1}`}>
                <div className="exam-tile-no">0{idx + 1}</div>
                <div className="exam-tile-body">
                  <h3>{ex.title}</h3>
                  <p>{ex.questions.length} soru{calc ? ` · ${calc} hesaplama sorusu` : ""}{ex.blueprint ? " · Belirtke tablolu (kapsam geçerliliği)" : ""}</p>
                  {ex.desc && <p className="exam-tile-desc">{ex.desc}</p>}
                  <div className="exam-tile-bar"><div style={{ width: `${(s.answered / s.total) * 100}%` }} /></div>
                  <div className="exam-tile-meta">
                    <span>{s.answered}/{s.total} cevaplandı</span>
                    <span className="ok">{s.correct} doğru</span>
                    <span className="bad">{s.wrong} yanlış</span>
                  </div>
                </div>
                <span className="exam-tile-cta">{s.answered ? (s.answered === s.total ? "Sonuçlar" : "Devam et") : "Başla"} →</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const answer = (qi, choice) => {
    setAnswers({ ...answers, [exam.id]: { ...ans, [qi]: choice } });
    if (choice !== exam.questions[qi].a) {
      setMistakes({ ...mistakes, [`${exam.id}:${qi}`]: { course: course.key, examId: exam.id, qi, chosen: choice, ts: Date.now() } });
    }
  };

  const reset = () => {
    const next = { ...answers };
    delete next[exam.id];
    setAnswers(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const list = exam.questions
    .map((q, i) => ({ q, i }))
    .filter(({ q, i }) => {
      if (filter === "open") return ans[i] === undefined;
      if (filter === "wrong") return ans[i] !== undefined && ans[i] !== q.a;
      if (filter === "calc") return !!q.s;
      return true;
    });

  const pct = Math.round((sc.correct / sc.total) * 100);
  const done = sc.answered === sc.total;

  return (
    <div>
      <button className="btn btn-ghost back" onClick={() => setExamId(null)} data-testid="exam-back"><ArrowLeft size={15} /> Sınav listesi</button>
      <div className="exam-sticky" data-testid="exam-header">
        <div className="exam-sticky-row">
          <div>
            <span className="eyebrow">{course.code}</span>
            <h2 className="exam-title">{exam.title}</h2>
          </div>
          <div className="exam-score">
            <span className="sc ok" data-testid="exam-correct-count">{sc.correct} D</span>
            <span className="sc bad" data-testid="exam-wrong-count">{sc.wrong} Y</span>
            <span className="sc">{sc.total - sc.answered} B</span>
          </div>
          <div className="exam-actions">
            <button className="btn btn-ghost" onClick={reset} data-testid="exam-reset"><RotateCcw size={15} /> Sıfırla</button>
            <button className="btn btn-accent" onClick={onPdf} disabled={pdfBusy} data-testid="exam-pdf-btn">
              {pdfBusy ? <Loader2 size={15} className="spin" /> : <FileDown size={15} />} Karneyi PDF İndir
            </button>
          </div>
        </div>
        <div className="exam-progress">
          {exam.questions.map((q, i) => (
            <a key={i} href={`#q-${i}`} className={`dot ${ans[i] === undefined ? "" : ans[i] === q.a ? "ok" : "bad"}`} title={`Soru ${i + 1}`}>{i + 1}</a>
          ))}
        </div>
      </div>

      {exam.blueprint && (
        <div className="blueprint" data-testid="exam-blueprint">
          <span className="blueprint-title"><Target size={14} /> Belirtke tablosu · kapsam geçerliliği</span>
          <div className="blueprint-cells">
            {exam.blueprint.map(([label, n]) => (
              <span key={label} className="blueprint-cell"><b>{n}</b><span>{label}</span></span>
            ))}
          </div>
        </div>
      )}

      {done && (
        <div className="result-card" data-testid="exam-result">
          <Trophy size={28} />
          <div>
            <h3>Sınav tamamlandı: {sc.correct}/{sc.total} · %{pct}</h3>
            <p>{pct >= 85 ? "Harika! Vizeye hazırsın." : pct >= 60 ? "İyi gidiyor. Yanlışlarını Hata Defterinden tekrar et." : "Notlara ve hap bilgilere dönüp yanlışlarını tekrar çöz."} Puan (100 üzerinden): <strong>{sc.correct * 4}</strong></p>
          </div>
          {sc.wrong > 0 && <button className="btn btn-ghost" onClick={goMistakes} data-testid="result-go-mistakes"><NotebookPen size={15} /> Hata Defterine git</button>}
        </div>
      )}

      <div className="chips exam-filters">
        {[["all", "Tüm sorular"], ["open", "Cevaplanmamış"], ["wrong", "Yanlışlarım"], ...(exam.questions.some((q) => q.s) ? [["calc", "Hesaplama soruları"]] : [])].map(([k, l]) => (
          <button key={k} className={`chip ${filter === k ? "active" : ""}`} onClick={() => setFilter(k)} data-testid={`exam-filter-${k}`}>{l}</button>
        ))}
        <span className="chip-note"><Target size={14} /> Her doğru 4 puan</span>
      </div>

      <div className="q-list">
        {list.length === 0 && <div className="empty"><p>Bu filtrede soru yok.</p></div>}
        {list.map(({ q, i }) => (
          <div id={`q-${i}`} key={i} className="q-anchor">
            <QuestionCard q={q} number={i + 1} chosen={ans[i]} onAnswer={(c) => answer(i, c)} />
          </div>
        ))}
      </div>
    </div>
  );
}
